// @ts-ignore
import { JitsiMeeting } from "@jitsi/vue-sdk";
import { defineComponent, ref, computed, onMounted, inject, watch, onBeforeUnmount } from "vue";
import { Container } from "aurelia-dependency-injection";
import { useRoute } from "vue-router";
import moment from "moment";

import { useApp } from "../App/useApp";
import {
    IConversationUser,
    Participant,
    Room,
    Rooms,
    OutgoingMessage,
    ISendMessageQuery
} from "../../resources-vue/vue-interfaces/messaging-service/i-message";
import { sliceRoomId } from "../../common/vue-helpers/modifiers/value-modifier";
import eventBus from "../../utilities/eventBus";
import { capitalizeFirstLetter } from "../../utilities/capitalizeFirstLetter";
import { isMobileScreenSize } from "../../utilities/isMobileScreenSize";
import { ToastrService } from "../../services/toastr.service";
import { UserManager } from "oidc-client";
import { ConversationRoomTypeEnum } from "../../enums/ConversationRoomTypeEnum";
import config from "../../common/config";
import { ICallControls } from "../../resources-vue/vue-interfaces/messaging-service/i-notification";
import { MessageService } from "../../services/message-service";
import { useConversationStore } from "../MessageCenter/store/useConversationStore";
import { useMessagesStore } from "../MessageCenter/store/useMessagesStore";
import { useCallStore } from "../MessageCenter/store/useCallStore";

export default defineComponent({
    props: {
        users: {
            type: Array as () => IConversationUser[],
            default: []
        }
    },
    components: {
        JitsiMeeting
    },

    setup(props) {
        const dialogRef = inject("dialogRef") as any;
        const meetingTimer = ref("");
        const domain = ref(`${config.vcpServerURL.replace("https://", "")}`);
        const dialogReference = ref(dialogRef);
        const apiRef = ref<any>(null);
        const participants = ref<Participant[]>([]);
        const iframeRef = ref();
        const participantsPaneOpen = ref<boolean>(false);
        const otherParticipantsOpen = ref<boolean>(false);
        const toastrService = ref<ToastrService | null>(null);
        const userManager = ref<UserManager | null>(null);
        const cachedConversationId = ref<string>("");
        const appStore = useApp();
        const convStore = useConversationStore();
        const msgStore = useMessagesStore();
        const callStore = useCallStore();
        const messageService = ref<MessageService | null>(null);
        let timerInterval: NodeJS.Timeout;

        const route = useRoute();

        const configOverwrite = computed(() => {
            const isVideoMeeting = dialogReference.value.data.meetingType === ConversationRoomTypeEnum.VideoCall;
            let callControls: ICallControls;
            if (dialogReference.value.data.callControls) {
                callControls = dialogReference.value.data.callControls;
            } else {
                callControls = {
                    audioEnabled: true,
                    videoEnabled: dialogReference.value.data.meetingType === ConversationRoomTypeEnum.VideoCall
                };
            }

            return {
                startWithAudioMuted: !callControls.audioEnabled,
                startWithVideoMuted: !callControls.videoEnabled,
                breakoutRooms: {
                    hideAddRoomButton: true
                },
                prejoinConfig: {
                    enabled: false
                },
                disableModeratorIndicator: true,
                disableProfile: true,
                disabledSounds: ["REACTION_SOUND"]
            };
        });

        const timer = computed(() => meetingTimer.value);
        const roomInfo = computed(() => dialogReference.value.data.roomInfo);
        const jwt = computed(() => dialogReference.value.data.roomInfo?.token);
        const meetingParticipantsExceptAuthUser = computed(() => dialogReference.value.data.users);
        const loading = computed(() => dialogReference.value.data.loading);
        const callStartTime = dialogReference.value.data.callStartTime;
        const roomName = computed(() => dialogReference.value.data.roomName);
        const meetingWidth = !isMobileScreenSize() ? "600px" : "100%";

        const invitedParticipants = computed(() => {
            const meetingParticipants = meetingParticipantsExceptAuthUser.value.map(
                (participant: IConversationUser) => ({
                    role: "",
                    displayName: `${capitalizeFirstLetter(`${participant.firstName} ${participant.lastName}`)}`,
                    avatarUrl: "",
                    id: ""
                })
            );
            const joinedParticipants = participants.value;

            return meetingParticipants.filter((participant: Participant) => {
                return !joinedParticipants.some(
                    (joinedParticipant) =>
                        joinedParticipant.displayName.toLowerCase() === participant.displayName.toLowerCase()
                );
            });
        });

        const jitsiOptions = computed(() => ({
            startAudioOnly: false,
            configOverwrite: configOverwrite.value,
            interfaceConfigOverwrite: {
                TOOLBAR_BUTTONS: [
                    "microphone",
                    "camera",
                    "desktop",
                    "tileview",
                    "fullscreen",
                    "raisehand",
                    "settings",
                    "hangup",
                    "participants-pane",
                    "chat"
                ]
            },
            userInfo: {
                displayName: "John Doe",
                email: "john.doe@example.com"
            },
            parentNode: document.querySelector(".jitsi-video")
        }));

        const handleJitsiMeetJSLoaded = () => {
            console.log("Jitsi Meet JS loaded");
        };

        const handleConferenceJoined = () => {
            updateParticipants();
        };

        const handleConferenceLeft = () => {
            dialogReference.value.close();
        };

        const handleError = (error: any) => {
            console.error("Jitsi Meet error:", error);
        };

        const getPariticipantsPaneStatus = ({ open }: { open: boolean }) => {
            participantsPaneOpen.value = open;
            if (open) {
                otherParticipantsOpen.value = true;
            } else {
                otherParticipantsOpen.value = false;
            }
        };

        const sendMessage = async (message: string) => {
            const convId = callStore.currentCallDetails.convId;
            if (!callStore.currentCallDetails.isUserOnCall && !convId.length) {
                return;
            }
            let messageQuery: ISendMessageQuery = {
                message: message,
                isImportant: false,
                assets: [],
                senderDisplayFirstname: appStore.loggedInUserDetails.firstName,
                senderDisplayLastname: appStore.loggedInUserDetails.lastName,
                replyToMessageId: null,
                placeHoldersList: []
            };

            try {
                const messagesResult = await messageService.value.sendMessage(convId, messageQuery);
                const msgDetails = (await messageService.value.getMessageById(convId, messagesResult.id)).messageData;
                const msg = {
                    text: msgDetails.text,
                    placeHoldersList: msgDetails.placeHoldersList,
                    modifiedOn: new Date(msgDetails.createdOn).toISOString().slice(0, -1),
                    isImportant: msgDetails.isImportant,
                    id: msgDetails.id,
                    unread: 0
                };

                if (convStore.activeConversationId === convId) {
                    // if conv is opened, mark msg as read
                    msgDetails.isRead = true;
                }
                msgStore.addMessage(convId, msgDetails);
                await convStore.updateSideNavMessage(convId, msg);
                convStore.moveConversationToTop(convId);
            } catch (e: any) {
                console.error(e);
            }
        };

        const handleOutgoingMessage = async (event: OutgoingMessage) => {
            const { message } = event;
            await sendMessage(message);
        };

        const handleApiReady = (externalApi: any) => {
            apiRef.value = externalApi;
            apiRef.value.addEventListener("participantJoined", updateParticipants);
            apiRef.value.addEventListener("participantLeft", updateParticipants);
            apiRef.value.addEventListener("videoConferenceJoined", () => {
                updateParticipants();
                startTimer();
            });
            apiRef.value.addEventListener("videoConferenceLeft", () => {
                clearInterval(timerInterval);
                updateParticipants();
            });
            apiRef.value.addEventListener("participantsPaneToggled", getPariticipantsPaneStatus);

            const iframe = apiRef.value.getIFrame();
            iframeRef.value = iframe;
            apiRef.value.addEventListener("outgoingMessage", handleOutgoingMessage);
            apiRef.value.executeCommand("setNoiseSuppressionEnabled", {
                // Enable noise suppression by default
                enabled: true
            });
            // Listen for jitsi chat message updates
            apiRef.value.on("chatUpdated", (event) => {
                // Check if the chat panel is currently open
                if (event.isOpen && callStore.currentCallDetails) {
                    const conv = convStore.conversations[callStore.currentCallDetails.convId];
                    if (conv?.unread > 0) {
                        convStore.markConversationAsRead(callStore.currentCallDetails.convId);
                    }
                }
            });
        };

        const startTimer = () => {
            const callDate = moment.utc(callStartTime).local();
            timerInterval = setInterval(() => {
                const difference = moment(moment()).diff(moment(callDate), "seconds");
                const duration = moment.duration(difference, "seconds");
                meetingTimer.value =
                    difference >= 3600
                        ? moment.utc(duration.asMilliseconds()).format("HH:mm:ss")
                        : moment.utc(duration.asMilliseconds()).format("mm:ss");
            }, 1000);
        };

        const updateParticipants = () => {
            if (apiRef.value) {
                apiRef.value
                    .getRoomsInfo()
                    .then((roomsInfo: Rooms) => {
                        if (roomsInfo.rooms[0]?.id) {
                            const currentRoom = roomsInfo.rooms.find(
                                (room: Room) => room.id.split("@")[0] === roomInfo.value.roomName
                            );
                            if (currentRoom) {
                                participants.value = currentRoom.participants;
                            }
                        }
                    })
                    .catch((error: unknown) => {
                        console.error("Error fetching room info:", error);
                    });
            }
        };

        const toggleOtherParticipants = () => {
            otherParticipantsOpen.value = !otherParticipantsOpen.value;
        };

        const handleEndCurrentCall = () => {
            cancel();
        };

        const cancel = () => {
            eventBus.emit("dialogOpened", false);
            dialogReference.value.close();
            apiRef.value.executeCommand("hangup");
        };

        watch(
            () => roomInfo,
            (newRoomInfo) => {
                if (newRoomInfo) {
                    updateParticipants();
                }
            }
        );

        onMounted(() => {
            cachedConversationId.value = route.params.id as string;
            toastrService.value = Container.instance.get(ToastrService);
            userManager.value = Container.instance.get(UserManager);
            messageService.value = Container.instance.get(MessageService);
            eventBus.on("endCurrentCall", handleEndCurrentCall);
        });

        onBeforeUnmount(() => {
            eventBus.off("endCurrentCall", handleEndCurrentCall);
        });

        return {
            domain,
            loading,
            configOverwrite,
            jwt,
            handleJitsiMeetJSLoaded,
            handleConferenceJoined,
            handleConferenceLeft,
            handleError,
            handleApiReady,
            toggleOtherParticipants,
            cancel,
            jitsiOptions,
            participants,
            invitedParticipants,
            participantsPaneOpen,
            otherParticipantsOpen,
            roomInfo,
            timer,
            sliceRoomId,
            handleEndCurrentCall,
            meetingWidth,
            roomName
        };
    }
});
